import * as Yup from 'yup';
import { AfterSchoolClubProgramme } from 'Src/models/clubProgramme';
import {
  ClubProgrammePreBookingAccess,
  ClubProgrammeSelectionMode,
  ClubProgrammeStatus
} from 'Src/types/clubProgramme';

export type ClubProgrammeFormAudienceInvitations = 'PARENT' | 'STUDENT' | 'BOTH';
export type ClubProgrammeFormAudienceGender = 'MALE' | 'FEMALE';

export interface ClubProgrammeFormValues {
  name: string;
  description: string;
  status: ClubProgrammeStatus;
  selectionMode: ClubProgrammeSelectionMode;
  preBookingAccess: ClubProgrammePreBookingAccess;
  bookingOpensAt: Date | null;
  bookingClosesAt: Date | null;
  allowChangesAfterSubmit: boolean;

  audienceAges: number[];
  audienceGenders: ClubProgrammeFormAudienceGender[];
  audienceInvitations: ClubProgrammeFormAudienceInvitations;

  priorityMax: number;
}

export const clubProgrammeFormSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required('Please enter a programme name')
    .min(2, 'Programme name is too short')
    .max(80, 'Programme name is too long'),

  description: Yup.string()
    .trim()
    .max(2000, 'Description is too long'),

  bookingOpensAt: Yup.date()
    .nullable()
    .required('Booking opens is required'),

  bookingClosesAt: Yup.date()
    .nullable()
    .required('Booking closes is required')
    .when('bookingOpensAt', ([opensAt], schema) => {
      return opensAt ? schema.min(opensAt, 'Booking closes must be after Booking opens') : schema;
    }),

  audienceAges: Yup.array()
    .of(Yup.number().integer())
    .min(1, 'Please select at least one age group')
    .required('Please select at least one age group'),

  audienceGenders: Yup.array()
    .of(Yup.mixed<ClubProgrammeFormAudienceGender>().oneOf(['MALE', 'FEMALE']))
    .min(1, 'Please select at least one gender')
    .required('Please select at least one gender'),

  audienceInvitations: Yup.mixed<ClubProgrammeFormAudienceInvitations>()
    .oneOf(['PARENT', 'STUDENT', 'BOTH'])
    .required('Please select who should receive reminders'),

  priorityMax: Yup.number()
    .integer('Priority limit must be a whole number')
    .min(2, 'Priority limit must be between 2 and 10')
    .max(10, 'Priority limit must be between 2 and 10')
    .required('Priority limit is required')
});

export const buildClubProgrammeFormDefaults = (
  initial?: Partial<AfterSchoolClubProgramme>
): ClubProgrammeFormValues => {
  const rawAudienceAges = (initial as any)?.audience?.ages;
  const rawAudienceGenders = (initial as any)?.audience?.genders;
  const rawAudienceInvitations = (initial as any)?.audience?.invitations;
  const rawPriorityMax = Number((initial as any)?.selectionRules?.priorityRange?.max);
  const rawAllowChangesAfterSubmit = (initial as any)?.booking?.allowChangesAfterSubmit;

  const audienceAges = Array.isArray(rawAudienceAges)
    ? rawAudienceAges
        .map((x: any) => Number(x))
        .filter((x: number) => Number.isFinite(x))
        .map((x: number) => Math.trunc(x))
    : [];

  const audienceGenders = Array.isArray(rawAudienceGenders)
    ? rawAudienceGenders.filter((x: any) => x === 'MALE' || x === 'FEMALE')
    : ['MALE', 'FEMALE'];

  const audienceInvitations =
    rawAudienceInvitations === 'PARENT' || rawAudienceInvitations === 'STUDENT' || rawAudienceInvitations === 'BOTH'
      ? rawAudienceInvitations
      : 'PARENT';

  const priorityMax =
    Number.isFinite(rawPriorityMax) && rawPriorityMax >= 2 && rawPriorityMax <= 10 ? Math.trunc(rawPriorityMax) : 10;

  return {
    name: (initial?.name || '').trim(),
    description: String((initial as any)?.description || ''),
    status: (initial as any)?.status || 'DRAFT',
    selectionMode: (initial as any)?.selectionRules?.mode || 'SINGLE',
    preBookingAccess: (initial as any)?.booking?.preBookingAccess || 'VIEW',
    bookingOpensAt: (initial as any)?.booking?.opensAt ? new Date((initial as any).booking.opensAt) : null,
    bookingClosesAt: (initial as any)?.booking?.closesAt ? new Date((initial as any).booking.closesAt) : null,
    allowChangesAfterSubmit: typeof rawAllowChangesAfterSubmit === 'boolean' ? rawAllowChangesAfterSubmit : false,

    audienceAges,
    audienceGenders,
    audienceInvitations,
    priorityMax
  };
};

export const buildClubProgrammeFormInitialValues = (
  initial?: Partial<AfterSchoolClubProgramme>,
  initialValues?: Partial<ClubProgrammeFormValues>
): ClubProgrammeFormValues => {
  return {
    ...buildClubProgrammeFormDefaults(initial),
    ...(initialValues || {})
  };
};
